import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  deprovisionTenant,
  getPlans,
  getTenant,
  getTenantHealth,
  impersonateRedeemBase,
  impersonateTenant,
  listImpersonation,
  provisionTenant,
  resumeTenant,
  revokeImpersonation,
  setSalesStage,
  suspendTenant,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate } from "../lib/format";
import { isLead, SALES_STAGES, type ImpersonationScope, type ProvisioningStepRow, type SalesStage, type TenantDomainRow } from "../lib/types";
import { TopBar } from "../components/Layout";
import { StatusBadge } from "../components/Badge";
import { AuditTable } from "../components/AuditTable";
import { ContractsPanel } from "../components/ContractsPanel";
import { CopyButton } from "../components/CopyButton";
import { Alert, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";

export function TenantDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const { data, loading, error, reload } = useApi((signal) => getTenant(id, signal), [id]);
  const plansApi = useApi((signal) => getPlans(signal), []);
  const plans = plansApi.data?.plans ?? [];

  const suspend = useMutation(suspendTenant);
  const resume = useMutation(resumeTenant);
  const [showDeprovision, setShowDeprovision] = useState(false);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [showProvision, setShowProvision] = useState(false);
  const [grantsNonce, setGrantsNonce] = useState(0);
  const [banner, setBanner] = useState<string | null>(null);

  if (loading) {
    return (
      <>
        <TopBar crumbs="Tenants / …" />
        <div className="content">
          <Loading label="Loading tenant…" />
        </div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <TopBar crumbs="Tenants / …" />
        <div className="content">
          <ErrorState error={error ?? "Tenant not found."} onRetry={reload} />
          <p style={{ marginTop: 16 }}>
            <Link to="/tenants">← Back to tenants</Link>
          </p>
        </div>
      </>
    );
  }

  const { tenant, domains, audit } = data;
  const lead = isLead(tenant);
  // A real (provisioned) suspended customer can be resumed; a never-provisioned
  // lead also sits in 'suspended' but should be Provisioned, not "Resumed".
  const canSuspend = tenant.status === "active";
  const canResume = tenant.status === "suspended" && !lead;
  const busy = suspend.pending || resume.pending;

  async function onSuspend() {
    const res = await suspend.run(id);
    if (res) {
      setBanner(`Tenant suspended — edge routing now blocks it.`);
      reload();
    }
  }
  async function onResume() {
    const res = await resume.run(id);
    if (res) {
      setBanner(`Tenant resumed — edge routing restored.`);
      reload();
    }
  }

  return (
    <>
      <TopBar
        crumbs={`Tenants / ${tenant.name}`}
        actions={
          <>
            <button className="btn" onClick={reload}>
              Refresh
            </button>
            {lead ? (
              <button className="btn primary" onClick={() => setShowProvision(true)}>
                Provision
              </button>
            ) : null}
            {canSuspend ? (
              <button className="btn" onClick={onSuspend} disabled={busy}>
                {suspend.pending ? <Spinner label="Suspending…" /> : "Suspend"}
              </button>
            ) : null}
            {canResume ? (
              <button className="btn primary" onClick={onResume} disabled={busy}>
                {resume.pending ? <Spinner label="Resuming…" /> : "Resume"}
              </button>
            ) : null}
            <button
              className="btn"
              onClick={() => setShowImpersonate(true)}
              disabled={tenant.status !== "active"}
              title="Audited, time-boxed support access into this tenant"
            >
              Impersonate
            </button>
            <button
              className="btn danger"
              onClick={() => setShowDeprovision(true)}
              disabled={tenant.status === "deprovisioning" || tenant.status === "deleted"}
            >
              Deprovision
            </button>
          </>
        }
      />
      <div className="content">
        <p style={{ marginTop: 0 }}>
          <Link to="/tenants">← Tenants</Link>
        </p>

        <div className="page-head">
          <div>
            <h1 style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {tenant.name} <StatusBadge status={tenant.status} />
            </h1>
            <div className="sub mono">{tenant.slug}.actionconnect.com</div>
          </div>
        </div>

        {banner ? <Alert kind="success">{banner}</Alert> : null}
        {suspend.error ? <Alert kind="error">{suspend.error}</Alert> : null}
        {resume.error ? <Alert kind="error">{resume.error}</Alert> : null}

        <div className="grid-2">
          <div className="card">
            <div className="card-head">
              <h2>Overview</h2>
            </div>
            <div className="card-body">
              <dl className="kv">
                <dt>Tenant ID</dt>
                <dd className="mono">
                  {tenant.id}
                  <CopyButton value={tenant.id} label="tenant ID" />
                </dd>
                <dt>Slug</dt>
                <dd className="mono">
                  {tenant.slug}
                  <CopyButton value={tenant.slug} label="slug" />
                </dd>
                <dt>Status</dt>
                <dd>
                  <StatusBadge status={tenant.status} />
                  {lead ? <span className="pill" style={{ marginLeft: 8 }}>Lead — not provisioned</span> : null}
                </dd>
                {tenant.sales_stage ? (
                  <>
                    <dt>Sales stage</dt>
                    <dd>
                      <SalesStageSelect
                        tenantId={tenant.id}
                        value={tenant.sales_stage}
                        onChanged={reload}
                      />
                    </dd>
                  </>
                ) : null}
                {tenant.contact_email ? (
                  <>
                    <dt>Contact</dt>
                    <dd>
                      {tenant.contact_name ? `${tenant.contact_name} · ` : ""}
                      <span className="mono">{tenant.contact_email}</span>
                    </dd>
                  </>
                ) : null}
                <dt>Plan</dt>
                <dd className="mono">{tenant.plan_id ?? "—"}</dd>
                <dt>Region</dt>
                <dd>{tenant.region}</dd>
                <dt>Account ref</dt>
                <dd>{tenant.account_ref}</dd>
                <dt>Created</dt>
                <dd>{formatDate(tenant.created_at)}</dd>
                <dt>Updated</dt>
                <dd>{formatDate(tenant.updated_at)}</dd>
              </dl>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Resources</h2>
            </div>
            <div className="card-body">
              <dl className="kv">
                <dt>D1 database</dt>
                <dd className="mono">
                  {tenant.d1_database_name ?? "—"}
                  {tenant.d1_database_id ? (
                    <div className="faint" style={{ fontSize: "0.75rem" }}>
                      {tenant.d1_database_id}
                      <CopyButton value={tenant.d1_database_id} label="D1 database ID" />
                    </div>
                  ) : null}
                </dd>
                <dt>R2 bucket</dt>
                <dd className="mono">{tenant.r2_bucket ?? "—"}</dd>
                <dt>KV prefix</dt>
                <dd className="mono">{tenant.kv_settings_prefix ?? "—"}</dd>
                <dt>Primary host</dt>
                <dd className="mono">{tenant.primary_host ?? "—"}</dd>
                <dt>Inbound email</dt>
                <dd className="mono">{tenant.inbound_email ?? "—"}</dd>
              </dl>
            </div>
          </div>
        </div>

        <ProvisioningCard tenantId={tenant.id} tenantStatus={tenant.status} />

        <DomainsCard domains={domains} />

        <ContractsPanel tenantId={tenant.id} plans={plans} />

        <ImpersonationCard tenantId={tenant.id} refreshKey={grantsNonce} />

        <div className="card">
          <div className="card-head">
            <h2>Audit trail</h2>
            <Link to={`/audit?tenant_id=${tenant.id}`} className="btn ghost small">
              View full log
            </Link>
          </div>
          <div className="card-body tight">
            <AuditTable rows={audit} showTenant={false} />
          </div>
        </div>
      </div>

      {showDeprovision ? (
        <DeprovisionModal
          tenantName={tenant.name}
          tenantSlug={tenant.slug}
          onClose={() => setShowDeprovision(false)}
          onConfirmed={() => {
            setShowDeprovision(false);
            navigate("/tenants");
          }}
          tenantId={id}
        />
      ) : null}

      {showImpersonate ? (
        <ImpersonateModal
          tenantId={id}
          tenantName={tenant.name}
          tenantHost={tenant.primary_host ?? `${tenant.slug}.actionconnect.com`}
          onClose={() => setShowImpersonate(false)}
          onGranted={() => setGrantsNonce((n) => n + 1)}
        />
      ) : null}

      {showProvision ? (
        <ProvisionModal
          tenantId={id}
          tenantName={tenant.name}
          defaultEmail={tenant.contact_email ?? ""}
          defaultName={tenant.contact_name ?? ""}
          onClose={() => setShowProvision(false)}
          onProvisioned={() => {
            setShowProvision(false);
            setBanner("Provisioning enqueued — resources are spinning up.");
            reload();
          }}
        />
      ) : null}
    </>
  );
}

/** Inline sales-stage selector (used on the lead's overview). */
function SalesStageSelect({
  tenantId,
  value,
  onChanged,
}: {
  tenantId: string;
  value: string;
  onChanged: () => void;
}) {
  const { run, pending } = useMutation(setSalesStage);
  async function change(stage: SalesStage) {
    if (await run(tenantId, stage)) onChanged();
  }
  return (
    <select
      value={value}
      disabled={pending}
      onChange={(e) => change(e.target.value as SalesStage)}
      aria-label="Sales stage"
    >
      {SALES_STAGES.map((s) => (
        <option key={s} value={s}>
          {SALES_STAGE_LABELS[s]}
        </option>
      ))}
      {!SALES_STAGES.includes(value as SalesStage) ? <option value={value}>{value}</option> : null}
    </select>
  );
}

/** Human labels for the pipeline stages. */
const SALES_STAGE_LABELS: Record<string, string> = {
  new: "New",
  contacted: "Contacted",
  demo: "Demo",
  contract_sent: "Contract sent",
  signed: "Signed",
  provisioned: "Provisioned",
  won: "Won",
  lost: "Lost",
};

function ProvisionModal({
  tenantId,
  tenantName,
  defaultEmail,
  defaultName,
  onClose,
  onProvisioned,
}: {
  tenantId: string;
  tenantName: string;
  defaultEmail: string;
  defaultName: string;
  onClose: () => void;
  onProvisioned: () => void;
}) {
  const [adminEmail, setAdminEmail] = useState(defaultEmail);
  const [adminName, setAdminName] = useState(defaultName);
  const { run, pending, error } = useMutation(provisionTenant);

  async function onConfirm() {
    if (!adminEmail.trim()) return;
    const res = await run(tenantId, {
      adminEmail: adminEmail.trim(),
      ...(adminName.trim() ? { adminName: adminName.trim() } : {}),
    });
    if (res) onProvisioned();
  }

  return (
    <Modal
      title={`Provision ${tenantName}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" onClick={onConfirm} disabled={pending || !adminEmail.trim()}>
            {pending ? <Spinner label="Enqueuing…" /> : "Provision now"}
          </button>
        </>
      }
    >
      <Alert kind="info">
        Spins up this lead's real resources — D1, R2, routing and email — and emails the admin their
        sign-in. The lead moves to the <strong>Provisioned</strong> stage.
      </Alert>
      {error ? <Alert kind="error">{error}</Alert> : null}
      <label className="field">
        <span className="label">Admin name</span>
        <input type="text" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Admin" />
      </label>
      <label className="field">
        <span className="label">Admin email</span>
        <input
          type="email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          placeholder="admin@college.edu"
          autoFocus
          required
        />
      </label>
    </Modal>
  );
}

function ImpersonateModal({
  tenantId,
  tenantName,
  tenantHost,
  onClose,
  onGranted,
}: {
  tenantId: string;
  tenantName: string;
  tenantHost: string;
  onClose: () => void;
  onGranted: () => void;
}) {
  const [reason, setReason] = useState("");
  const [scope, setScope] = useState<ImpersonationScope>("read_write");
  const { run, pending, error } = useMutation(impersonateTenant);
  const [opened, setOpened] = useState<string | null>(null);

  async function onStart() {
    if (!reason.trim()) return;
    const res = await run(tenantId, reason.trim(), { scope });
    if (res) {
      // Redeem at the tenant edge: token → tenant session, then into the app. The
      // redeem endpoint resolves the tenant from the token (host-agnostic). The base
      // is resolved to the SAME environment as this console (see
      // impersonateRedeemBase) so a staging console never opens a prod URL; the edge
      // then redirects into the app via its APP_BASE_URL.
      const edgeBase = impersonateRedeemBase(tenantHost);
      const url = `${edgeBase}/api/impersonate/redeem?token=${encodeURIComponent(res.token)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      setOpened(url);
      onGranted();
    }
  }

  return (
    <Modal
      title={`Impersonate ${tenantName}`}
      onClose={onClose}
      footer={
        opened ? (
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button className="btn" onClick={onClose} disabled={pending}>
              Cancel
            </button>
            <button className="btn primary" onClick={onStart} disabled={pending || !reason.trim()}>
              {pending ? <Spinner label="Starting…" /> : "Start impersonation"}
            </button>
          </>
        )
      }
    >
      {opened ? (
        <Alert kind="success">
          Impersonation session opened in a new tab — <strong>audited</strong> and expires in 30
          minutes. If the tab didn't open,{" "}
          <a href={opened} target="_blank" rel="noopener noreferrer">
            open it manually
          </a>
          .
        </Alert>
      ) : (
        <>
          <Alert kind="info">
            Support access into <strong>{tenantName}</strong> as its tenant admin. This is{" "}
            <strong>time-boxed (30 min)</strong> and <strong>fully audited</strong> — the reason and
            every action are recorded against your staff account.
          </Alert>
          {error ? <Alert kind="error">{error}</Alert> : null}
          <label className="field">
            <span className="label">Reason (recorded)</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              autoFocus
              placeholder="e.g. Investigating ticket-sync issue for support case #1234"
              required
            />
          </label>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
            <span className="label">Access scope</span>
            <div className="seg" role="radiogroup" aria-label="Access scope">
              <button
                type="button"
                className={`seg-btn${scope === "read_only" ? " active" : ""}`}
                role="radio"
                aria-checked={scope === "read_only"}
                onClick={() => setScope("read_only")}
              >
                Read-only
              </button>
              <button
                type="button"
                className={`seg-btn${scope === "read_write" ? " active" : ""}`}
                role="radio"
                aria-checked={scope === "read_write"}
                onClick={() => setScope("read_write")}
              >
                Read-write
              </button>
            </div>
            <span className="hint">
              {scope === "read_only"
                ? "View-only support access — no changes can be made in the tenant."
                : "Full support access — you can make changes on the tenant's behalf."}
            </span>
          </fieldset>
        </>
      )}
    </Modal>
  );
}

function ImpersonationCard({ tenantId, refreshKey }: { tenantId: string; refreshKey: number }) {
  const { data, loading, error, reload } = useApi(
    (signal) => listImpersonation(tenantId, signal),
    [tenantId, refreshKey],
  );
  const revoke = useMutation(revokeImpersonation);
  const grants = data?.grants ?? [];
  const now = Math.floor(Date.now() / 1000);

  async function onRevoke(grantId: string) {
    if (await revoke.run(grantId)) reload();
  }

  function status(g: { revoked_at: number | null; expires_at: number }) {
    if (g.revoked_at) return { label: "Revoked", cls: "pill" };
    if (g.expires_at <= now) return { label: "Expired", cls: "pill" };
    return { label: "Active", cls: "pill ok" };
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Impersonation grants</h2>
        <button className="btn ghost small" onClick={reload} disabled={loading}>
          Refresh
        </button>
      </div>
      <div className="card-body tight">
        {revoke.error ? <Alert kind="error">{revoke.error}</Alert> : null}
        {loading ? (
          <Loading label="Loading grants…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : grants.length === 0 ? (
          <div className="state muted">No impersonation grants for this tenant.</div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Reason</th>
                <th>Granted</th>
                <th>Expires</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => {
                const s = status(g);
                return (
                  <tr key={g.id}>
                    <td>{g.reason}</td>
                    <td>{formatDate(g.created_at)}</td>
                    <td>{formatDate(g.expires_at)}</td>
                    <td>
                      <span className={s.cls}>{s.label}</span>
                    </td>
                    <td>
                      {s.label === "Active" ? (
                        <button
                          className="btn danger small"
                          onClick={() => onRevoke(g.id)}
                          disabled={revoke.pending}
                        >
                          Revoke
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Latest provisioning job + its step-by-step journal from the control-plane
 *  state machine (provisioning_jobs / provisioning_steps), with open issues. */
function ProvisioningCard({
  tenantId,
  tenantStatus,
}: {
  tenantId: string;
  tenantStatus: string;
}) {
  const { data, loading, error, reload } = useApi(
    (signal) => getTenantHealth(tenantId, signal),
    [tenantId, tenantStatus],
  );

  // Light polling: while the latest job is still queued/running, re-pull the
  // health so steps advance without the operator hitting Refresh.
  const jobStatus = data?.latest_job?.status;
  const inProgress = jobStatus === "queued" || jobStatus === "running";
  useEffect(() => {
    if (!inProgress) return;
    const t = window.setInterval(() => reload(), 5000);
    return () => window.clearInterval(t);
  }, [inProgress, reload]);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Provisioning</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {data?.latest_job ? (
            <span className="pill mono">
              {data.latest_job.kind} · {data.latest_job.status}
              {data.latest_job.attempts > 1 ? ` · ${data.latest_job.attempts} attempts` : ""}
            </span>
          ) : null}
          <button className="btn ghost small" onClick={reload} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading provisioning status…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : !data ? null : (
          <>
            {data.open_issues.map((issue) => (
              <Alert key={issue} kind="error">
                {issue}
              </Alert>
            ))}
            {data.latest_job ? (
              <>
                <p className="muted" style={{ marginTop: 0 }}>
                  Latest <strong>{data.latest_job.kind}</strong> job{" "}
                  <span className="mono">{data.latest_job.id}</span>
                  <CopyButton value={data.latest_job.id} label="job ID" /> — created{" "}
                  {formatDate(data.latest_job.created_at)}, last update{" "}
                  {formatDate(data.latest_job.updated_at)}.
                </p>
                {data.steps.length === 0 ? (
                  <div className="state muted">No steps recorded for this job yet.</div>
                ) : (
                  <ol className="timeline">
                    {data.steps.map((s) => (
                      <ProvisioningStep key={s.id} step={s} />
                    ))}
                  </ol>
                )}
              </>
            ) : (
              <div className="state muted">
                No provisioning job recorded in the control plane for this tenant (it may predate
                the provisioning journal). Current status is shown in the overview above.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ProvisioningStep({ step }: { step: ProvisioningStepRow }) {
  const duration =
    step.started_at && step.finished_at ? Math.max(0, step.finished_at - step.started_at) : null;
  return (
    <li className={`timeline-item ${step.status}`}>
      <div className="timeline-row">
        <span className="mono">{step.step}</span>
        <span className={`pill timeline-status ${step.status}`}>{step.status}</span>
        {duration != null ? (
          <span className="faint">{duration < 1 ? "<1s" : `${duration}s`}</span>
        ) : null}
      </div>
      {step.error ? (
        <div className="timeline-error mono" title={step.error}>
          {step.error}
        </div>
      ) : null}
    </li>
  );
}

function DomainsCard({ domains }: { domains: TenantDomainRow[] }) {
  return (
    <div className="card">
      <div className="card-head">
        <h2>Domains</h2>
      </div>
      <div className="card-body tight">
        {domains.length === 0 ? (
          <div className="state muted">No domains mapped yet.</div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Hostname</th>
                <th>Surface</th>
                <th>Kind</th>
                <th>Verified</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id}>
                  <td className="mono">{d.hostname}</td>
                  <td>{d.surface}</td>
                  <td>{d.kind}</td>
                  <td>{d.verified ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DeprovisionModal({
  tenantId,
  tenantName,
  tenantSlug,
  onClose,
  onConfirmed,
}: {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const [confirm, setConfirm] = useState("");
  const { run, pending, error } = useMutation(deprovisionTenant);
  const matches = confirm.trim() === tenantSlug;

  async function onConfirm() {
    if (!matches) return;
    const res = await run(tenantId);
    if (res) onConfirmed();
  }

  return (
    <Modal
      title="Deprovision tenant"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn danger solid" onClick={onConfirm} disabled={!matches || pending}>
            {pending ? <Spinner label="Enqueuing…" /> : "Deprovision tenant"}
          </button>
        </>
      }
    >
      <Alert kind="error">
        This permanently tears down <strong>{tenantName}</strong> — its D1 database, R2 bucket,
        routing and email. This cannot be undone.
      </Alert>
      <label className="field">
        <span className="label">
          Type the slug <code>{tenantSlug}</code> to confirm
        </span>
        <input
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={tenantSlug}
          autoFocus
          autoComplete="off"
        />
      </label>
      {error ? <Alert kind="error">{error}</Alert> : null}
    </Modal>
  );
}
